import React, { useState, useCallback, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Upload, AlertCircle, CheckCircle, XCircle, FileText, Loader2 } from 'lucide-react';
import axios from 'axios';
import { tokenStorage } from '../../lib/tokenStorage';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Required headers that must exist in the CSV for it to be valid
const REQUIRED_HEADERS = ['Deal Name', 'Advance Amount', 'Factor Rate'];

// Also accept "Funding Amount" as a legacy alias for "Advance Amount"
function normaliseHeaders(headers) {
  return headers.map(h => h.trim() === 'Funding Amount' ? 'Advance Amount' : h);
}
// Headers we recognise (case-insensitive match)
const KNOWN_HEADERS = [
  'Deal ID', 'Status', 'Deal Name', 'Merchant Name', 'Funding Date',
  'Advance Amount', 'Factor Rate', 'Term (Days)', 'Term', 'Fee Amount',
  'Payments Made', 'Remaining Balance (RTR)', 'Remaining Balance',
];

// ---- CSV PARSER ---- handles quoted fields with commas/newlines
function parseCSV(text) {
  const lines = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (ch === '"') {
      // Keep quotes in the line so splitLine can parse them correctly.
      // Only track inQuotes here to handle newlines inside quoted multiline fields.
      current += ch;
      if (inQuotes && text[i + 1] === '"') {
        // Escaped quote "" — keep both chars, skip the second one
        current += text[i + 1];
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (ch === '\n' && !inQuotes) {
      lines.push(current);
      current = '';
    } else if (ch === '\r') {
      // ignore \r
    } else {
      current += ch;
    }
  }
  if (current) lines.push(current);

  function splitLine(line) {
    const cells = [];
    let cell = '';
    let inQ = false;
    for (let i = 0; i < line.length; i++) {
      const c = line[i];
      if (c === '"') {
        if (inQ && line[i + 1] === '"') { cell += '"'; i++; }
        else inQ = !inQ;
      } else if (c === ',' && !inQ) {
        cells.push(cell); cell = '';
      } else {
        cell += c;
      }
    }
    cells.push(cell);
    return cells;
  }

  const nonEmpty = lines.filter(l => l.trim() !== '');
  if (nonEmpty.length < 2) return { headers: [], rows: [] };

  const headers = splitLine(nonEmpty[0]).map(h => h.trim());
  const rows = nonEmpty.slice(1).map(line => {
    const cells = splitLine(line);
    const obj = {};
    headers.forEach((h, i) => { obj[h] = (cells[i] ?? '').trim(); });
    return obj;
  });

  return { headers, rows };
}

function validateRow(row, idx) {
  const errs = [];
  if (!row['Deal Name']?.trim()) errs.push('Deal Name is required');
  if (!row['Advance Amount']?.trim()) errs.push('Advance Amount is required');
  if (!row['Factor Rate']?.trim()) errs.push('Factor Rate is required');
  const amt = parseFloat(row['Advance Amount']);
  const rate = parseFloat(row['Factor Rate']);
  if (row['Advance Amount'] && isNaN(amt)) errs.push('Advance Amount must be a number');
  if (row['Factor Rate'] && isNaN(rate)) errs.push('Factor Rate must be a number');
  return errs;
}

// Steps
const STEP_UPLOAD   = 'upload';
const STEP_PREVIEW  = 'preview';
const STEP_IMPORTING = 'importing';
const STEP_RESULTS  = 'results';

export default function DealsCSVImportModal({ open, onClose, onImportComplete }) {
  const [step, setStep]         = useState(STEP_UPLOAD);
  const [dragging, setDragging] = useState(false);
  const [fileError, setFileError] = useState('');
  const [parsed, setParsed]     = useState(null); // { headers, rows, rowErrors, existingIds }
  const [results, setResults]   = useState(null); // { created, updated, skipped, errors }
  const fileInputRef            = useRef(null);

  const reset = useCallback(() => {
    setStep(STEP_UPLOAD);
    setFileError('');
    setParsed(null);
    setResults(null);
    setDragging(false);
  }, []);

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [reset, onClose]);

  // ---- File handling ----
  const processFile = useCallback(async (file) => {
    setFileError('');
    if (!file) return;
    if (!file.name.endsWith('.csv')) {
      setFileError('Only .csv files are supported.');
      return;
    }

    const text = await file.text();
    let { headers, rows } = parseCSV(text);

    // Normalise legacy header names (e.g. "Funding Amount" → "Advance Amount")
    headers = normaliseHeaders(headers);
    rows = rows.map(row => {
      if ('Funding Amount' in row) {
        row['Advance Amount'] = row['Funding Amount'];
        delete row['Funding Amount'];
      }
      return row;
    });

    if (headers.length === 0 || rows.length === 0) {
      setFileError('The file appears to be empty or unreadable.');
      return;
    }

    // Validate required headers exist
    const missing = REQUIRED_HEADERS.filter(req =>
      !headers.some(h => h.toLowerCase() === req.toLowerCase())
    );
    if (missing.length > 0) {
      setFileError(
        `Unrecognised file format. Missing required columns: ${missing.join(', ')}. ` +
        'Please use a file exported from this system.'
      );
      return;
    }

    // Validate each row
    const rowErrors = rows.map((row, i) => ({ row: i + 1, errors: validateRow(row, i) }))
      .filter(r => r.errors.length > 0);

    // Check which Deal IDs already exist
    const dealIds = rows.map(r => (r['Deal ID'] || '').trim()).filter(Boolean);
    let existingIdSet = new Set();
    if (dealIds.length > 0) {
      try {
        const token = tokenStorage.getToken();
        const res = await axios.post(
          `${API_URL}/api/deals/csv-check-ids`,
          { deal_ids: dealIds },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        existingIdSet = new Set(res.data.existing_ids || []);
      } catch {
        // If check fails, proceed without status — import will handle it
      }
    }

    setParsed({ headers, rows, rowErrors, existingIdSet });
    setStep(STEP_PREVIEW);
  }, []);

  const onDrop = useCallback((e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) processFile(file);
  }, [processFile]);

  const onFileChange = useCallback((e) => {
    const file = e.target.files[0];
    if (file) processFile(file);
    e.target.value = '';
  }, [processFile]);

  // ---- Import ----
  const handleImport = useCallback(async () => {
    if (!parsed) return;
    setStep(STEP_IMPORTING);

    try {
      const token = tokenStorage.getToken();
      const res = await axios.post(
        `${API_URL}/api/deals/csv-import`,
        { rows: parsed.rows },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setResults(res.data);
      setStep(STEP_RESULTS);
      if (onImportComplete) onImportComplete();
    } catch (err) {
      setResults({
        created: 0,
        updated: 0,
        skipped: parsed.rows.length,
        errors: [{ row: 'all', error: err.response?.data?.detail || 'Import request failed' }],
      });
      setStep(STEP_RESULTS);
    }
  }, [parsed, onImportComplete]);

  // ---- Preview helpers ----
  const previewRows    = parsed?.rows.slice(0, 5) ?? [];
  const previewHeaders = parsed?.headers ?? [];
  const totalRows      = parsed?.rows.length ?? 0;
  const existingCount  = parsed ? parsed.rows.filter(r => parsed.existingIdSet.has((r['Deal ID'] || '').trim())).length : 0;
  const newCount       = totalRows - existingCount;
  const invalidRows    = parsed?.rowErrors.length ?? 0;
  const importableRows = totalRows - invalidRows;

  const hasErrors      = (parsed?.rowErrors.length ?? 0) > 0;

  // --- STEP: UPLOAD ---
  if (step === STEP_UPLOAD) return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg" data-testid="csv-import-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5 text-green-600" />
            Import Deals from CSV
          </DialogTitle>
        </DialogHeader>

        <p className="text-sm text-gray-500">
          Upload a deals CSV file previously exported from this system.
          Existing deals (matched by Deal ID) will be updated; new rows will be created.
        </p>

        {/* Drop zone */}
        <div
          className={`mt-2 border-2 border-dashed rounded-xl p-10 text-center cursor-pointer transition-colors ${
            dragging ? 'border-green-400 bg-green-50' : 'border-gray-300 hover:border-green-400 hover:bg-gray-50'
          }`}
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          onClick={() => fileInputRef.current?.click()}
          data-testid="csv-dropzone"
        >
          <FileText className="h-10 w-10 text-gray-300 mx-auto mb-3" />
          <p className="text-sm font-medium text-gray-600">
            {dragging ? 'Release to upload' : 'Drag & drop a .csv file here'}
          </p>
          <p className="text-xs text-gray-400 mt-1">or click to browse</p>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            className="hidden"
            onChange={onFileChange}
            data-testid="csv-file-input"
          />
        </div>

        {fileError && (
          <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2.5 text-sm text-red-700" data-testid="csv-file-error">
            <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
            {fileError}
          </div>
        )}

        <div className="flex justify-end pt-1">
          <Button variant="outline" onClick={handleClose} data-testid="csv-import-cancel-btn">Cancel</Button>
        </div>
      </DialogContent>
    </Dialog>
  );

  // --- STEP: PREVIEW ---
  if (step === STEP_PREVIEW) return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="csv-import-preview">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-blue-600" />
            Preview Import
          </DialogTitle>
        </DialogHeader>

        {/* Summary bar */}
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 text-center">
            <p className="text-xl font-bold text-blue-700">{totalRows}</p>
            <p className="text-xs text-blue-600 mt-0.5">Total Deals Found</p>
          </div>
          <div className="bg-green-50 border border-green-100 rounded-lg p-3 text-center">
            <p className="text-xl font-bold text-green-700">{newCount}</p>
            <p className="text-xs text-green-600 mt-0.5">New (will create)</p>
          </div>
          <div className="bg-amber-50 border border-amber-100 rounded-lg p-3 text-center">
            <p className="text-xl font-bold text-amber-700">{existingCount}</p>
            <p className="text-xs text-amber-600 mt-0.5">Already exist (will update)</p>
          </div>
        </div>

        {/* Error flag */}
        {hasErrors && (
          <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2.5 text-sm" data-testid="csv-preview-errors">
            <AlertCircle className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />
            <div>
              <p className="font-medium text-red-700">{invalidRows} row{invalidRows !== 1 ? 's' : ''} have validation errors and will be skipped:</p>
              <ul className="mt-1 space-y-0.5">
                {parsed.rowErrors.map(({ row, errors }) => (
                  <li key={row} className="text-red-600 text-xs">
                    Row {row}: {errors.join('; ')}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {/* Preview table — first 5 rows */}
        <div>
          <p className="text-xs font-semibold text-gray-400 uppercase mb-2">
            Preview (first {Math.min(5, totalRows)} of {totalRows} rows)
          </p>
          <div className="overflow-x-auto rounded-lg border border-gray-200">
            <table className="w-full text-xs">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  {previewHeaders.map(h => (
                    <th key={h} className="px-3 py-2 text-left font-semibold text-gray-500 whitespace-nowrap">
                      {h}
                      {REQUIRED_HEADERS.includes(h) && (
                        <span className="ml-1 text-red-400">*</span>
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {previewRows.map((row, i) => {
                  const rowErr = parsed.rowErrors.find(e => e.row === i + 1);
                  return (
                    <tr
                      key={i}
                      className={`border-b border-gray-100 ${rowErr ? 'bg-red-50' : ''}`}
                      data-testid={`csv-preview-row-${i}`}
                    >
                      {previewHeaders.map(h => (
                        <td key={h} className="px-3 py-2 text-gray-700 whitespace-nowrap max-w-[160px] truncate">
                          {row[h] || <span className="text-gray-300">—</span>}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {totalRows > 5 && (
            <p className="text-xs text-gray-400 mt-1.5 text-right">
              + {totalRows - 5} more rows not shown
            </p>
          )}
        </div>

        <div className="flex gap-3 pt-2 border-t border-gray-200">
          <Button variant="outline" onClick={reset} className="flex-1" data-testid="csv-preview-back-btn">
            Back
          </Button>
          <Button
            className="flex-1 bg-green-600 hover:bg-green-700"
            onClick={handleImport}
            disabled={importableRows === 0}
            data-testid="csv-preview-import-btn"
          >
            <Upload className="h-4 w-4 mr-2" />
            Import {importableRows} Deal{importableRows !== 1 ? 's' : ''}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );

  // --- STEP: IMPORTING ---
  if (step === STEP_IMPORTING) return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent className="max-w-sm" data-testid="csv-importing">
        <div className="flex flex-col items-center py-8 gap-4">
          <Loader2 className="h-10 w-10 text-green-600 animate-spin" />
          <p className="text-base font-medium text-gray-700">Importing deals…</p>
          <p className="text-sm text-gray-400">Please do not close this window.</p>
        </div>
      </DialogContent>
    </Dialog>
  );

  // --- STEP: RESULTS ---
  if (step === STEP_RESULTS && results) return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md" data-testid="csv-import-results">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {results.skipped === 0 && results.errors.length === 0
              ? <CheckCircle className="h-5 w-5 text-green-600" />
              : <AlertCircle className="h-5 w-5 text-amber-500" />}
            Import Complete
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-3 gap-3">
          <div className="bg-green-50 border border-green-100 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold text-green-700" data-testid="import-result-created">{results.created}</p>
            <p className="text-xs text-green-600 mt-0.5">Created</p>
          </div>
          <div className="bg-amber-50 border border-amber-100 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold text-amber-700" data-testid="import-result-updated">{results.updated}</p>
            <p className="text-xs text-amber-600 mt-0.5">Updated</p>
          </div>
          <div className="bg-red-50 border border-red-100 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold text-red-700" data-testid="import-result-skipped">{results.skipped}</p>
            <p className="text-xs text-red-600 mt-0.5">Skipped (errors)</p>
          </div>
        </div>

        {results.errors.length > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 max-h-48 overflow-y-auto">
            <p className="text-xs font-semibold text-red-700 mb-2 uppercase tracking-wide">
              Skipped rows
            </p>
            <ul className="space-y-1">
              {results.errors.map((e, i) => (
                <li key={i} className="text-xs text-red-600">
                  {e.row !== 'all' ? `Row ${e.row}: ` : ''}{e.error}
                </li>
              ))}
            </ul>
          </div>
        )}

        {results.created === 0 && results.updated === 0 ? (
          <p className="text-sm text-gray-500">No deals were imported successfully.</p>
        ) : (
          <p className="text-sm text-gray-600">
            <span className="font-medium text-green-700">{results.created + results.updated}</span> deals processed successfully.
            The deals list has been refreshed.
          </p>
        )}

        <div className="flex gap-3 pt-2 border-t border-gray-200">
          {results.created + results.updated < (parsed?.rows.length ?? 0) && (
            <Button variant="outline" className="flex-1" onClick={reset} data-testid="import-try-again-btn">
              Import Another File
            </Button>
          )}
          <Button
            className="flex-1 bg-gray-800 hover:bg-gray-900"
            onClick={handleClose}
            data-testid="import-done-btn"
          >
            Done
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );

  return null;
}
